import { LETTER_GROUP_FALLBACK } from './constants';

/**
 * Phone line of a contact row: "[Country Calling Code] [Phone number]", in full.
 *
 * @param {object} contact Contact returned by the contact APIs.
 * @returns {string} e.g. "+243 812345678".
 */
export const formatContactPhone = ({ countryCode = '', mobileNumber = '' } = {}) => {
    const dial = countryCode ? `+${String(countryCode).replace(/^\+/, '')}` : '';
    return [dial, mobileNumber].filter(Boolean).join(' ');
};

/**
 * Name shown for a contact: the caller's address-book name (nickName), then the contact's
 * profile name (displayName), then the phone line.
 *
 * @param {object} contact Contact returned by the contact APIs.
 * @returns {string} Name to show.
 */
export const getContactName = contact =>
    contact?.nickName?.trim() || contact?.displayName?.trim() || formatContactPhone(contact) || contact?.userId || '';

/**
 * Groups contacts under the first letter of their name, keeping the server's A→Z order.
 *
 * @param {object[]} contacts Contacts already sorted by name.
 * @returns {{letter: string, contacts: object[]}[]} One entry per letter.
 */
export const groupContactsByLetter = contacts => {
    const groups = [];
    contacts.forEach(contact => {
        const first = getContactName(contact).charAt(0).toLocaleUpperCase();
        const letter = /\p{L}/u.test(first) ? first : LETTER_GROUP_FALLBACK;
        const last = groups[groups.length - 1];
        if (last && last.letter === letter) {
            last.contacts.push(contact);
        } else {
            groups.push({ letter, contacts: [contact] });
        }
    });
    return groups;
};

/**
 * Sorts contacts by name, A→Z, the way the member list of the group details shows them.
 *
 * @param {object[]} contacts Contacts in any order.
 * @returns {object[]} A new, sorted array.
 */
export const sortContactsByName = contacts =>
    [...contacts].sort((a, b) =>
        getContactName(a).localeCompare(getContactName(b), undefined, { sensitivity: 'base' }),
    );

/**
 * Shape the conversation pane expects for a 1-1 roster entry.
 *
 * @param {object} contact Contact returned by the contact APIs.
 * @returns {object} Roster entry.
 */
export const toRosterEntry = contact => ({
    ...contact,
    userJid: contact.jid,
    name: getContactName(contact),
    nickName: contact.displayName || '',
    isAdminBlocked: !!contact.isAdminBlocked,
});
